import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import scenariosData from "@/data/scenarios.json";
import { logger } from "@/utils/logger";
import { getStageData, buildShuffledOptions, resolveOptionData, defaultScoreForElement } from "@/utils/assessmentStages";

const TOTAL_STAGES = 3;

/**
 * Owns the complete assessment state machine: scenario/stage pointers,
 * submitted choices, navigation history, path-change flow, and final submission.
 *
 * Returned shape is grouped so page consumers can pass-through sections
 * without destructuring noise.
 */
export const useAssessmentFlow = ({ assessmentId, apiBase, user, updateUser, navigate }) => {
  const [currentScenarioIndex, setCurrentScenarioIndex] = useState(0);
  const [currentStage, setCurrentStage] = useState(1);
  const [selectedOption, setSelectedOption] = useState("");
  const [previousChoices, setPreviousChoices] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [showScenarioComplete, setShowScenarioComplete] = useState(false);
  const [showFinalConfirmation, setShowFinalConfirmation] = useState(false);
  const [showPathChangeWarning, setShowPathChangeWarning] = useState(false);
  const [pendingOptionChange, setPendingOptionChange] = useState(null);
  const [navigationHistory, setNavigationHistory] = useState([]);
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [furthestProgress, setFurthestProgress] = useState({ scenarioIndex: 0, stage: 1 });

  const scenarios = scenariosData.scenarios;
  const currentScenario = scenarios[currentScenarioIndex];
  const totalScenarios = scenarios.length;

  // Scroll to top on stage change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentScenarioIndex, currentStage]);

  const currentStageChoice = previousChoices.find(
    c => c.scenario_id === currentScenario?.id && c.stage === currentStage
  );
  const hasExistingChoice = !!currentStageChoice;

  // Keep the selected radio in sync with any stored choice
  useEffect(() => {
    setSelectedOption(hasExistingChoice ? currentStageChoice.element : "");
  }, [currentScenarioIndex, currentStage, hasExistingChoice, currentStageChoice]);

  // Compute review mode
  useEffect(() => {
    const currentProgress = currentScenarioIndex * TOTAL_STAGES + currentStage;
    const maxProgress = furthestProgress.scenarioIndex * TOTAL_STAGES + furthestProgress.stage;
    setIsReviewMode(currentProgress < maxProgress);
  }, [currentScenarioIndex, currentStage, furthestProgress]);

  const stageData = getStageData(currentScenario, currentStage, previousChoices);

  const shuffledOptions = useMemo(
    () => buildShuffledOptions(stageData, currentScenario, currentStage),
    [stageData, currentScenario, currentStage]
  );

  const handleOptionSelect = (element) => {
    if (hasExistingChoice && currentStageChoice.element !== element && currentStage < 3) {
      setPendingOptionChange(element);
      setShowPathChangeWarning(true);
      return;
    }
    setSelectedOption(element);
  };

  const handleConfirmPathChange = () => {
    setSelectedOption(pendingOptionChange);
    // Drop current & later choices in this scenario
    setPreviousChoices(previousChoices.filter(
      c => !(c.scenario_id === currentScenario.id && c.stage >= currentStage)
    ));
    setNavigationHistory(navigationHistory.filter(
      h => !(h.scenarioIndex === currentScenarioIndex && h.stage >= currentStage)
    ));
    setShowPathChangeWarning(false);
    setPendingOptionChange(null);
    toast.info("Path changed. Please answer the remaining questions in this scenario.");
  };

  const handleCancelPathChange = () => {
    setShowPathChangeWarning(false);
    setPendingOptionChange(null);
    setSelectedOption(currentStageChoice?.element || "");
  };

  const recordChoice = (choice) => {
    const existingIndex = previousChoices.findIndex(
      c => c.scenario_id === currentScenario.id && c.stage === currentStage
    );
    const newChoices = [...previousChoices];
    if (existingIndex >= 0) newChoices[existingIndex] = choice;
    else newChoices.push(choice);
    setPreviousChoices(newChoices);

    setNavigationHistory([...navigationHistory, {
      scenarioIndex: currentScenarioIndex,
      stage: currentStage,
      choice
    }]);
  };

  const updateFurthestProgress = () => {
    const newProgress = currentStage >= TOTAL_STAGES
      ? { scenarioIndex: currentScenarioIndex + 1, stage: 1 }
      : { scenarioIndex: currentScenarioIndex, stage: currentStage + 1 };
    const currentMax = furthestProgress.scenarioIndex * TOTAL_STAGES + furthestProgress.stage;
    const newMax = newProgress.scenarioIndex * TOTAL_STAGES + newProgress.stage;
    if (newMax > currentMax) setFurthestProgress(newProgress);
  };

  const advanceAfterSubmit = () => {
    if (currentStage < TOTAL_STAGES) {
      setCurrentStage(currentStage + 1);
      if (user?.username) {
        axios.post(`${apiBase}/auth/update-state`, {
          username: user.username,
          last_scenario: currentScenario.id
        }).catch(() => {});
      }
    } else if (currentScenarioIndex < totalScenarios - 1) {
      setShowScenarioComplete(true);
    } else {
      setShowFinalConfirmation(true);
    }
  };

  const handleNext = async () => {
    if (!selectedOption || !stageData) {
      toast.error("Please select an option");
      return;
    }
    setSubmitting(true);
    try {
      const optionData = resolveOptionData(stageData, selectedOption);
      const score = optionData?.score || defaultScoreForElement(selectedOption);
      const choice = {
        scenario_id: currentScenario.id,
        stage: currentStage,
        element: selectedOption,
        code: optionData?.code || stageData.code || stageData.id,
        score
      };
      await axios.post(`${apiBase}/assessment/${assessmentId}/choice`, choice);
      recordChoice(choice);
      updateFurthestProgress();
      advanceAfterSubmit();
    } catch (error) {
      logger.error("Error submitting choice:", error);
      toast.error("Failed to submit choice. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleContinueToNextScenario = () => {
    setShowScenarioComplete(false);
    setCurrentScenarioIndex(currentScenarioIndex + 1);
    setCurrentStage(1);
  };

  const handleBack = () => {
    if (currentStage > 1) {
      setCurrentStage(currentStage - 1);
    } else if (currentScenarioIndex > 0) {
      setCurrentScenarioIndex(currentScenarioIndex - 1);
      setCurrentStage(TOTAL_STAGES);
    } else {
      toast.error("You're at the beginning of the assessment");
    }
  };

  const completeAssessment = async () => {
    try {
      await axios.post(`${apiBase}/assessment/${assessmentId}/complete`);
      if (user?.username) {
        await axios.post(`${apiBase}/auth/update-state`, {
          username: user.username,
          assessment_state: "report"
        });
        updateUser({ assessment_state: "report" });
      }
      toast.success("Assessment completed!");
      navigate(`/report/${assessmentId}`);
    } catch (error) {
      logger.error("Error completing assessment:", error);
      toast.error("Failed to complete assessment");
    }
  };

  const handleFinalSubmit = async () => {
    setShowFinalConfirmation(false);
    await completeAssessment();
  };

  const handleReviewResponses = () => {
    setShowFinalConfirmation(false);
    setCurrentScenarioIndex(0);
    setCurrentStage(1);
    toast.info("Use the navigation buttons to review your responses. Use 'Skip to End' to return to submission.");
  };

  const isAssessmentFullyComplete = furthestProgress.scenarioIndex >= totalScenarios;
  const canSkipToEnd = isReviewMode && (
    furthestProgress.scenarioIndex > currentScenarioIndex ||
    (furthestProgress.scenarioIndex === currentScenarioIndex && furthestProgress.stage > currentStage) ||
    isAssessmentFullyComplete
  );

  const handleSkipToEnd = () => {
    if (isAssessmentFullyComplete) {
      setShowFinalConfirmation(true);
      return;
    }
    setCurrentScenarioIndex(furthestProgress.scenarioIndex);
    setCurrentStage(furthestProgress.stage);
  };

  const isLastQuestion =
    currentScenarioIndex === totalScenarios - 1 && currentStage === TOTAL_STAGES;

  return {
    // data
    scenarios,
    currentScenario,
    currentScenarioIndex,
    currentStage,
    totalScenarios,
    stageData,
    shuffledOptions,
    selectedOption,
    // flags
    submitting,
    isReviewMode,
    hasExistingChoice,
    canSkipToEnd,
    isLastQuestion,
    // dialog state
    showScenarioComplete,
    setShowScenarioComplete,
    showFinalConfirmation,
    setShowFinalConfirmation,
    showPathChangeWarning,
    setShowPathChangeWarning,
    // handlers
    handleOptionSelect,
    handleNext,
    handleBack,
    handleSkipToEnd,
    handleContinueToNextScenario,
    handleFinalSubmit,
    handleReviewResponses,
    handleConfirmPathChange,
    handleCancelPathChange,
  };
};
